type ColorValue = string | { [key: string]: ColorValue };

interface FontSizeOptions {
  lineHeight?: string;
  letterSpacing?: string;
  fontWeight?: string;
}

type FontSizeValue = string | [string, FontSizeOptions];

interface TailwindThemeExtend {
  colors: Record<string, ColorValue>;
  screens: Record<string, string>;
  spacing: Record<string, string>;
  fontFamily: Record<string, string[]>;
  fontSize: Record<string, FontSizeValue>;
  aspectRatio: Record<string, string>;
  boxShadow: Record<string, string>;
}

/**
 * Plain data describing this app's design tokens — ported 1:1 into the
 * `@theme` block in app/styles/tailwind.css (Tailwind v4 is CSS-first, so
 * this object is no longer a `tailwindcss` JS config; it's only read by
 * this file's authors as a source-of-truth reference and by tw-merge.ts).
 */
export const tailwindTheme: TailwindThemeExtend = {
  colors: {
    /* Brand */
    primary: "rgb(var(--color-primary))",
    secondary: "rgb(var(--color-secondary))",
    tertiary: "rgb(var(--color-tertiary))",
    black: "rgb(var(--pr-color-black))",
    white: "rgb(var(--pr-color-white))",

    /* General Use */
    green: {
      100: "rgb(var(--pr-color-green-100))",
      90: "rgb(var(--pr-color-green-90))",
      80: "rgb(var(--pr-color-green-80))",
      70: "rgb(var(--pr-color-green-70))",
      60: "rgb(var(--pr-color-green-60))",
      50: "rgb(var(--pr-color-green-50))",
      40: "rgb(var(--pr-color-green-40))",
      30: "rgb(var(--pr-color-green-30))",
      20: "rgb(var(--pr-color-green-20))",
      10: "rgb(var(--pr-color-green-10))",
    },
    red: {
      50: "rgb(var(--pr-color-red-50))",
      100: "rgb(var(--pr-color-red-100))",
      200: "rgb(var(--pr-color-red-200))",
      300: "rgb(var(--pr-color-red-300))",
      400: "rgb(var(--pr-color-red-400))",
      500: "rgb(var(--pr-color-red-500))",
      550: "rgb(var(--pr-color-red-550))",
      600: "rgb(var(--pr-color-red-600))",
      700: "rgb(var(--pr-color-red-700))",
      800: "rgb(var(--pr-color-red-800))",
      900: "rgb(var(--pr-color-red-900))",
      950: "rgb(var(--pr-color-red-950))",
    },
    yellow: {
      50: "rgb(var(--pr-color-yellow-50))",
      100: "rgb(var(--pr-color-yellow-100))",
      200: "rgb(var(--pr-color-yellow-200))",
      300: "rgb(var(--pr-color-yellow-300))",
      400: "rgb(var(--pr-color-yellow-400))",
      500: "rgb(var(--pr-color-yellow-500))",
      600: "rgb(var(--pr-color-yellow-600))",
      700: "rgb(var(--pr-color-yellow-700))",
      800: "rgb(var(--pr-color-yellow-800))",
      900: "rgb(var(--pr-color-yellow-900))",
      950: "rgb(var(--pr-color-yellow-950))",
    },
    "light-blue": {
      100: "rgb(var(--pr-color-light-blue-100))",
      90: "rgb(var(--pr-color-light-blue-90))",
      80: "rgb(var(--pr-color-light-blue-80))",
      70: "rgb(var(--pr-color-light-blue-70))",
      60: "rgb(var(--pr-color-light-blue-60))",
      50: "rgb(var(--pr-color-light-blue-50))",
      40: "rgb(var(--pr-color-light-blue-40))",
      30: "rgb(var(--pr-color-light-blue-30))",
      20: "rgb(var(--pr-color-light-blue-20))",
      10: "rgb(var(--pr-color-light-blue-10))",
    },
    beige: {
      50: "rgb(var(--pr-color-beige-50))",
      100: "rgb(var(--pr-color-beige-100))",
      200: "rgb(var(--pr-color-beige-200))",
      300: "rgb(var(--pr-color-beige-300))",
      400: "rgb(var(--pr-color-beige-400))",
      500: "rgb(var(--pr-color-beige-500))",
      600: "rgb(var(--pr-color-beige-600))",
      700: "rgb(var(--pr-color-beige-700))",
      800: "rgb(var(--pr-color-beige-800))",
      900: "rgb(var(--pr-color-beige-900))",
      950: "rgb(var(--pr-color-beige-950))",
    },
    sand: {
      100: "rgb(var(--pr-color-sand-100))",
      200: "rgb(var(--pr-color-sand-200))",
      300: "rgb(var(--pr-color-sand-300))",
    },
    plum: {
      500: "rgb(var(--pr-color-plum-500))",
    },
    mandy: {
      50: "rgb(var(--pr-color-mandy-50))",
      100: "rgb(var(--pr-color-mandy-100))",
      200: "rgb(var(--pr-color-mandy-200))",
      300: "rgb(var(--pr-color-mandy-300))",
      400: "rgb(var(--pr-color-mandy-400))",
      500: "rgb(var(--pr-color-mandy-500))",
      600: "rgb(var(--pr-color-mandy-600))",
      700: "rgb(var(--pr-color-mandy-700))",
      800: "rgb(var(--pr-color-mandy-800))",
      900: "rgb(var(--pr-color-mandy-900))",
      950: "rgb(var(--pr-color-mandy-950))",
    },
    "aqua-forest": {
      50: "rgb(var(--pr-color-aqua-forest-50))",
      100: "rgb(var(--pr-color-aqua-forest-100))",
      200: "rgb(var(--pr-color-aqua-forest-200))",
      300: "rgb(var(--pr-color-aqua-forest-300))",
      400: "rgb(var(--pr-color-aqua-forest-400))",
      500: "rgb(var(--pr-color-aqua-forest-500))",
      600: "rgb(var(--pr-color-aqua-forest-600))",
      700: "rgb(var(--pr-color-aqua-forest-700))",
      800: "rgb(var(--pr-color-aqua-forest-800))",
      900: "rgb(var(--pr-color-aqua-forest-900))",
      950: "rgb(var(--pr-color-aqua-forest-950))",
    },
    "amethyst-smoke": {
      50: "rgb(var(--pr-color-amethyst-smoke-50))",
      100: "rgb(var(--pr-color-amethyst-smoke-100))",
      200: "rgb(var(--pr-color-amethyst-smoke-200))",
      300: "rgb(var(--pr-color-amethyst-smoke-300))",
      400: "rgb(var(--pr-color-amethyst-smoke-400))",
      500: "rgb(var(--pr-color-amethyst-smoke-500))",
      600: "rgb(var(--pr-color-amethyst-smoke-600))",
      700: "rgb(var(--pr-color-amethyst-smoke-700))",
      800: "rgb(var(--pr-color-amethyst-smoke-800))",
      900: "rgb(var(--pr-color-amethyst-smoke-900))",
      950: "rgb(var(--pr-color-amethyst-smoke-950))",
    },
    "link-water": {
      50: "rgb(var(--pr-color-link-water-50))",
      100: "rgb(var(--pr-color-link-water-100))",
      200: "rgb(var(--pr-color-link-water-200))",
      300: "rgb(var(--pr-color-link-water-300))",
      400: "rgb(var(--pr-color-link-water-400))",
      500: "rgb(var(--pr-color-link-water-500))",
      600: "rgb(var(--pr-color-link-water-600))",
      700: "rgb(var(--pr-color-link-water-700))",
      800: "rgb(var(--pr-color-link-water-800))",
      900: "rgb(var(--pr-color-link-water-900))",
      950: "rgb(var(--pr-color-link-water-950))",
    },
    "out-of-stock": {
      bg: "#f3f2ef",
      label: "#c5bfb0",
    },
    neutral: {
      50: "rgb(var(--pr-color-neutral-50))",
      100: "rgb(var(--pr-color-neutral-100))",
      200: "rgb(var(--pr-color-neutral-200))",
      300: "rgb(var(--pr-color-neutral-300))",
      400: "rgb(var(--pr-color-neutral-400))",
      500: "rgb(var(--pr-color-neutral-500))",
      600: "rgb(var(--pr-color-neutral-600))",
      700: "rgb(var(--pr-color-neutral-700))",
      800: "rgb(var(--pr-color-neutral-800))",
      900: "rgb(var(--pr-color-neutral-900))",
      950: "rgb(var(--pr-color-neutral-950))",
    },

    /* System Colors */
    system: {
      success: "rgb(var(--color-system-success))",
      error: "rgb(var(--color-system-error))",
      warning: "rgb(var(--color-system-warning))",
      info: "rgb(var(--color-system-info))",
    },

    /* Background Colors */
    background: {
      page: "rgb(var(--pr-color-page))",
    },
    "surface-1": "rgb(var(--background-surface-1))",
    "surface-2": "rgb(var(--background-surface-2))",
    "surface-3": "rgb(var(--background-surface-3))",

    /* Text Colors */
    text: {
      body: "rgb(var(--text-body)",
      "less-emphasis-1": "rgb(var(--text-less-emphasis-1))",
      "less-emphasis-2": "rgb(var(--text-less-emphasis-2))",
      "less-emphasis-3": "rgb(var(--text-less-emphasis-3))",
    },

    /* Sale Colors */
    sale: {
      bg: "rgb(var(--sale-bg))",
      label: "rgb(var(--sale-label))",
      price: "rgb(var(--sale-price))",
    },

    /* Border Colors */
    border: {
      sharp: "rgb(var(--border-sharp))",
      light: "rgb(var(--border-light))",
    },

    /* Button Colors */
    "button-primary-default": {
      bg: "rgb(var(--button-primary-default-bg))",
      label: "rgb(var(--button-primary-default-label))",
    },
    "button-primary-hover": {
      bg: "rgb(var(--button-primary-hover-bg))",
      label: "rgb(var(--button-primary-hover-label))",
    },
    "button-primary-active": {
      bg: "rgb(var(--button-primary-active-bg))",
      label: "rgb(var(--button-primary-active-label))",
    },
    "button-primary-disabled": {
      bg: "rgb(var(--button-primary-disabled-bg))",
      label: "rgb(var(--button-primary-disabled-label))",
    },

    "button-secondary-default": {
      bg: "rgb(var(--button-secondary-default-bg))",
      label: "rgb(var(--button-secondary-default-label))",
    },
    "button-secondary-hover": {
      bg: "rgb(var(--button-secondary-hover-bg))",
      label: "rgb(var(--button-secondary-hover-label))",
    },
    "button-secondary-active": {
      bg: "rgb(var(--button-secondary-active-bg))",
      label: "rgb(var(--button-secondary-active-label))",
    },
    "button-secondary-disabled": {
      bg: "rgb(var(--button-secondary-disabled-bg))",
      label: "rgb(var(--button-secondary-disabled-label))",
    },

    "button-black-default": {
      bg: "rgb(var(--button-black-default-bg))",
      label: "rgb(var(--button-black-default-label))",
    },
    "button-black-hover": {
      bg: "rgb(var(--button-black-hover-bg))",
      label: "rgb(var(--button-black-hover-label))",
    },
    "button-black-active": {
      bg: "rgb(var(--button-black-active-bg))",
      label: "rgb(var(--button-black-active-label))",
    },
    "button-black-disabled": {
      bg: "rgb(var(--button-black-disabled-bg))",
      label: "rgb(var(--button-black-disabled-label))",
    },

    "button-white-default": {
      bg: "rgb(var(--button-white-default-bg))",
      label: "rgb(var(--button-white-default-label))",
    },
    "button-white-hover": {
      bg: "rgb(var(--button-white-hover-bg))",
      label: "rgb(var(--button-white-hover-label))",
    },
    "button-white-active": {
      bg: "rgb(var(--button-white-active-bg))",
      label: "rgb(var(--button-white-active-label))",
    },
    "button-white-disabled": {
      bg: "rgb(var(--button-white-disabled-bg))",
      label: "rgb(var(--button-white-disabled-label))",
    },
  },

  screens: {
    md: "768px",
    lg: "1025px",
    xl: "1366px",
    "2xl": "1680px",
    "page-max-width": "1440px",
  },

  spacing: {
    "component-spacing-none": "var(--component-spacing-none)",
    "component-spacing-5xs": "var(--component-spacing-5xs)",
    "component-spacing-4xs": "var(--component-spacing-4xs)",
    "component-spacing-3xs": "var(--component-spacing-3xs)",
    "component-spacing-2xs": "var(--component-spacing-2xs)",
    "component-spacing-xs": "var(--component-spacing-xs)",
    "component-spacing-sm": "var(--component-spacing-sm)",
    "component-spacing-md": "var(--component-spacing-md)",
    "component-spacing-lg": "var(--component-spacing-lg)",
    "component-spacing-xl": "var(--component-spacing-xl)",
    "component-spacing-2xl": "var(--component-spacing-2xl)",
    "component-spacing-3xl": "var(--component-spacing-3xl)",
    "component-spacing-4xl": "var(--component-spacing-4xl)",
    "component-spacing-5xl": "var(--component-spacing-5xl)",
    "component-spacing-6xl": "var(--component-spacing-6xl)",
    "component-spacing-7xl": "var(--component-spacing-7xl)",

    /* Component Max Widths */
    "component-max-w-none": "var(--component-max-width-none)",
    "component-max-w-sm": "var(--component-max-width-sm)",
    "component-max-w-md": "var(--component-max-width-md)",
    "component-max-w-lg": "var(--component-max-width-lg)",
    "component-max-w-xl": "var(--component-max-width-xl)",
  },

  fontFamily: {
    primary: ["var(--font-primary)"],
    secondary: ["var(--font-secondary)"],
  },

  fontSize: {
    /* Headings */
    "heading-2xl": [
      "var(--heading-2xl-font-size)",
      {
        lineHeight: "var(--heading-2xl-line-height)",
        letterSpacing: "var(--heading-2xl-letter-spacing)",
        fontWeight: "var(--heading-2xl-font-weight)",
      },
    ],
    "heading-xl": [
      "var(--heading-xl-font-size)",
      {
        lineHeight: "var(--heading-xl-line-height)",
        letterSpacing: "var(--heading-xl-letter-spacing)",
        fontWeight: "var(--heading-xl-font-weight)",
      },
    ],
    "heading-lg": [
      "var(--heading-lg-font-size)",
      {
        lineHeight: "var(--heading-lg-line-height)",
        letterSpacing: "var(--heading-lg-letter-spacing)",
        fontWeight: "var(--heading-lg-font-weight)",
      },
    ],
    "heading-md": [
      "var(--heading-md-font-size)",
      {
        lineHeight: "var(--heading-md-line-height)",
        letterSpacing: "var(--heading-md-letter-spacing)",
        fontWeight: "var(--heading-md-font-weight)",
      },
    ],
    "heading-sm": [
      "var(--heading-sm-font-size)",
      {
        lineHeight: "var(--heading-sm-line-height)",
        letterSpacing: "var(--heading-sm-letter-spacing)",
        fontWeight: "var(--heading-sm-font-weight)",
      },
    ],
    "heading-xs": [
      "var(--heading-xs-font-size)",
      {
        lineHeight: "var(--heading-xs-line-height)",
        letterSpacing: "var(--heading-xs-letter-spacing)",
        fontWeight: "var(--heading-xs-font-weight)",
      },
    ],
    "heading-2xs": [
      "var(--heading-2xs-font-size)",
      {
        lineHeight: "var(--heading-2xs-line-height)",
        letterSpacing: "var(--heading-2xs-letter-spacing)",
        fontWeight: "var(--heading-2xs-font-weight)",
      },
    ],
    "heading-display-sm": [
      "var(--heading-display-sm-font-size)",
      {
        lineHeight: "var(--heading-display-sm-line-height)",
        letterSpacing: "var(--heading-display-sm-letter-spacing)",
        fontWeight: "var(--heading-display-sm-font-weight)",
      },
    ],
    "heading-display-md": [
      "var(--heading-display-md-font-size)",
      {
        lineHeight: "var(--heading-display-md-line-height)",
        letterSpacing: "var(--heading-display-md-letter-spacing)",
        fontWeight: "var(--heading-display-md-font-weight)",
      },
    ],
    "heading-display-lg": [
      "var(--heading-display-lg-font-size)",
      {
        lineHeight: "var(--heading-display-lg-line-height)",
        letterSpacing: "var(--heading-display-lg-letter-spacing)",
        fontWeight: "var(--heading-display-lg-font-weight)",
      },
    ],

    /* Labels */
    "label-sm": [
      "var(--label-sm-font-size)",
      {
        lineHeight: "var(--label-sm-line-height)",
        letterSpacing: "var(--label-sm-letter-spacing)",
        fontWeight: "var(--label-sm-font-weight)",
      },
    ],
    "label-md": [
      "var(--label-md-font-size)",
      {
        lineHeight: "var(--label-md-line-height)",
        letterSpacing: "var(--label-md-letter-spacing)",
        fontWeight: "var(--label-md-font-weight)",
      },
    ],
    "label-lg": [
      "var(--label-lg-font-size)",
      {
        lineHeight: "var(--label-lg-line-height)",
        letterSpacing: "var(--label-lg-letter-spacing)",
        fontWeight: "var(--label-lg-font-weight)",
      },
    ],

    /* Body Text */
    "body-2xs": [
      "var(--body-2xs-font-size)",
      {
        lineHeight: "var(--body-2xs-line-height)",
        letterSpacing: "var(--body-2xs-letter-spacing)",
        fontWeight: "var(--body-2xs-font-weight)",
      },
    ],
    "body-xs": [
      "var(--body-xs-font-size)",
      {
        lineHeight: "var(--body-xs-line-height)",
        letterSpacing: "var(--body-xs-letter-spacing)",
        fontWeight: "var(--body-xs-font-weight)",
      },
    ],
    "body-sm": [
      "var(--body-sm-font-size)",
      {
        lineHeight: "var(--body-sm-line-height)",
        letterSpacing: "var(--body-sm-letter-spacing)",
        fontWeight: "var(--body-sm-font-weight)",
      },
    ],
    "body-md": [
      "var(--body-md-font-size)",
      {
        lineHeight: "var(--body-md-line-height)",
        letterSpacing: "var(--body-md-letter-spacing)",
        fontWeight: "var(--body-md-font-weight)",
      },
    ],
    "body-lg": [
      "var(--body-lg-font-size)",
      {
        lineHeight: "var(--body-lg-line-height)",
        letterSpacing: "var(--body-lg-letter-spacing)",
        fontWeight: "var(--body-lg-font-weight)",
      },
    ],

    /* Button Text Sizes */
    "button-sm": [
      "var(--button-sm-font-size)",
      {
        lineHeight: "var(--button-sm-line-height)",
        letterSpacing: "var(--button-sm-letter-spacing)",
        fontWeight: "var(--button-sm-font-weight)",
      },
    ],
    "button-md": [
      "var(--button-md-font-size)",
      {
        lineHeight: "var(--button-md-line-height)",
        letterSpacing: "var(--button-md-letter-spacing)",
        fontWeight: "var(--button-md-font-weight)",
      },
    ],
  },
  aspectRatio: {
    "product-image": "var(--aspect-ratio-product-image)",
    portrait: "var(--aspect-ratio-portrait)",
    tall: "var(--aspect-ratio-tall)",
    square: "var(--aspect-ratio-square)",
    landscape: "var(--aspect-ratio-landscape)",
    wide: "var(--aspect-ratio-wide)",
    "ultra-wide": "var(--aspect-ratio-ultra-wide)",
    panorama: "var(--aspect-ratio-panorama)",
    auto: "var(--aspect-ratio-auto)",
  },
  boxShadow: {
    "shadow-sharp": "var(--drop-shadow-sharp)",
  },
};
